import { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DndContext, DragOverlay, closestCorners } from '@dnd-kit/core';
import { Kanban } from 'lucide-react';

import type { Swimlane } from '@tasks/contracts';

import { listComponents } from '../../api/components';
import { listSprints } from '../../api/sprints';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { useAuth } from '../../auth/AuthProvider';
import { useHotkeys } from '../../hooks/useHotkeys';
import { useBoardKeyboardNav } from './useBoardKeyboardNav';
import { useProject } from '../../hooks/useProject';
import { useUiStore } from '../../stores/uiStore';
import { humanizeError } from '../../lib/errors';
import { useProjectSocket } from '../../realtime/useProjectSocket';
import { BoardColumnView } from './BoardColumnView';
import { BoardToolbar, type BoardFiltersState } from './BoardToolbar';
import { IssueCardView } from '../issue/IssueCardView';
import { IssuePanel } from '../issue/IssuePanel';
import { useBoard } from './useBoard';
import { useAllowedTransitions } from './useAllowedTransitions';
import { useBoardDnd } from './useBoardDnd';
import styles from './BoardPage.module.css';

const RAIL_ITEMS = 4;

/** `/projects/:key/board` — канбан с DnD, swimlanes, фильтрами и виртуализацией. */
export function BoardPage() {
  const { key = '', boardId = '' } = useParams<{ key: string; boardId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const setQuickCreateOpen = useUiStore((state) => state.setQuickCreateOpen);
  const { project } = useProject(key);

  const swimlane = (searchParams.get('swimlane') ?? 'none') as Swimlane;
  const sprintId = searchParams.get('sprintId');
  const assigneeId = searchParams.get('assigneeId');
  // `label` и `epicId` сервер принимал и раньше, но страница их не читала:
  // ссылка с фильтром открывала доску без фильтра (docs/13 §9).
  const label = searchParams.get('label');
  const epicId = searchParams.get('epicId');
  const componentId = searchParams.get('componentId');
  const q = searchParams.get('q') ?? '';
  const issueKey = searchParams.get('issue');

  const filters: BoardFiltersState = useMemo(
    () => ({
      swimlane,
      sprintId,
      assigneeId,
      componentId,
      q,
      onlyMine: searchParams.get('mine') === '1',
      onlyBugs: searchParams.get('type') === 'bug',
    }),
    [swimlane, sprintId, assigneeId, componentId, q, searchParams],
  );

  const boardParams = useMemo(
    () => ({
      boardId: boardId.length > 0 ? boardId : undefined,
      swimlane,
      sprintId: sprintId ?? undefined,
      assigneeId: assigneeId ?? undefined,
      componentId: componentId ?? undefined,
      label: label ?? undefined,
      epicId: epicId ?? undefined,
      q: q.length > 0 ? q : undefined,
      limitPerColumn: 50,
    }),
    [boardId, swimlane, sprintId, assigneeId, componentId, label, epicId, q],
  );

  const { board, isLoading, isError, error, refetch } = useBoard(key, boardParams);
  // Колонки и переходы — из workflow самой доски (docs/03 §1).
  const { map: allowedTransitions } = useAllowedTransitions(key, board?.board.workflowId);
  const dnd = useBoardDnd(key, board, allowedTransitions);

  useProjectSocket({ projectKey: key.length > 0 ? key : null, onPresence: () => undefined });

  const sprintsQuery = useQuery({
    queryKey: queryKeys.projects.sprints(key, 'all'),
    queryFn: ({ signal }) => listSprints(key, undefined, signal),
    enabled: key.length > 0,
  });

  // Справочник для фильтра «Компонент» и группировки по компонентам
  // (docs/13 §7). Архивные не нужны: новые задачи на них не ставят.
  const componentsQuery = useQuery({
    queryKey: queryKeys.projects.components(key),
    queryFn: ({ signal }) => listComponents(key, { signal }),
    enabled: key.length > 0,
  });

  const patchParams = useCallback(
    (patch: Readonly<Record<string, string | null>>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [name, value] of Object.entries(patch)) {
            if (value === null || value === '') {
              next.delete(name);
            } else {
              next.set(name, value);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleFilterChange = useCallback(
    (patch: Partial<BoardFiltersState>) => {
      const next: Record<string, string | null> = {};
      if (patch.swimlane !== undefined) {
        next['swimlane'] = patch.swimlane === 'none' ? null : patch.swimlane;
      }
      if (patch.sprintId !== undefined) {
        next['sprintId'] = patch.sprintId;
      }
      if (patch.assigneeId !== undefined) {
        next['assigneeId'] = patch.assigneeId;
      }
      if (patch.componentId !== undefined) {
        next['componentId'] = patch.componentId;
      }
      if (patch.q !== undefined) {
        next['q'] = patch.q;
      }
      if (patch.onlyMine !== undefined) {
        next['mine'] = patch.onlyMine ? '1' : null;
      }
      if (patch.onlyBugs !== undefined) {
        next['type'] = patch.onlyBugs ? 'bug' : null;
      }
      patchParams(next);
    },
    [patchParams],
  );

  const openIssue = useCallback(
    (nextKey: string) => patchParams({ issue: nextKey }),
    [patchParams],
  );

  useHotkeys(
    useMemo(
      () => ({
        b: () => patchParams({ issue: null }),
        escape: () => patchParams({ issue: null }),
      }),
      [patchParams],
    ),
  );

  const members = (project ? [user].filter((item): item is NonNullable<typeof item> => item !== null) : []) ?? [];

  // Пока идёт перетаскивание, стрелки принадлежат KeyboardSensor из @dnd-kit:
  // своя навигация по фокусу отключается, иначе она уводила бы фокус с
  // «взятой» карточки прямо во время перемещения (docs/06 §3.1).
  useBoardKeyboardNav(issueKey === null && dnd.activeCard === null);

  const visibleColumns = useMemo(() => {
    if (!board) {
      return [];
    }
    if (!filters.onlyMine && !filters.onlyBugs) {
      return board.columns;
    }
    return board.columns.map((column) => ({
      ...column,
      lanes: column.lanes.map((lane) => ({
        ...lane,
        issues: lane.issues.filter((issue) => {
          if (filters.onlyMine && issue.assignee?.id !== user?.id) {
            return false;
          }
          if (filters.onlyBugs && issue.type !== 'bug') {
            return false;
          }
          return true;
        }),
      })),
    }));
  }, [board, filters.onlyBugs, filters.onlyMine, user?.id]);

  const sprintOptions = sprintsQuery.data ?? [];
  const memberRefs = members.map((member) => ({
    id: member.id,
    login: member.login,
    displayName: member.displayName,
    avatarColor: member.avatarColor,
  }));

  return (
    <div className={styles['page']}>
      <BoardToolbar
        filters={filters}
        sprints={sprintOptions}
        components={componentsQuery.data ?? []}
        members={memberRefs}
        boardComponentIds={board?.board.componentIds ?? []}
        onChange={handleFilterChange}
        onReset={() =>
          patchParams({
            q: null,
            mine: null,
            type: null,
            assigneeId: null,
            componentId: null,
            label: null,
            epicId: null,
            sprintId: null,
          })
        }
        onCreateIssue={() => setQuickCreateOpen(true)}
      />

      {isLoading ? (
        <div className={styles['loading']}>
          <Skeleton variant="card" count={RAIL_ITEMS} />
        </div>
      ) : isError ? (
        <EmptyState
          icon={<Kanban size={20} />}
          title="Не удалось загрузить доску"
          description={humanizeError(error).message}
          action={
            <Button variant="primary" onClick={refetch}>
              Повторить
            </Button>
          }
        />
      ) : !board || board.total === 0 ? (
        <EmptyState
          icon={<Kanban size={20} />}
          title={filters.onlyMine || filters.onlyBugs || q.length > 0 ? 'Нет задач по текущим фильтрам' : 'На доске пока нет задач'}
          description={
            filters.onlyMine || filters.onlyBugs || q.length > 0
              ? 'Сбросьте фильтры или измените условия поиска.'
              : 'Создайте первую задачу — она появится в первой колонке.'
          }
          action={
            <Button variant="primary" onClick={() => setQuickCreateOpen(true)}>
              Создать задачу
            </Button>
          }
        />
      ) : (
        <DndContext
          sensors={dnd.sensors}
          collisionDetection={closestCorners}
          onDragStart={dnd.handleDragStart}
          onDragEnd={dnd.handleDragEnd}
          onDragCancel={dnd.handleDragCancel}
        >
          <div className={styles['columns']} data-testid="board">
            {visibleColumns.map((column) => (
              <BoardColumnView
                key={column.status.id}
                column={column}
                showLaneHeaders={board.swimlane !== 'none'}
                onOpenIssue={openIssue}
                isTransitionAllowed={(toStatusId) =>
                  dnd.activeCard !== null && dnd.isTransitionAllowed(dnd.activeCard, toStatusId)
                }
              />
            ))}
          </div>

          <DragOverlay dropAnimation={null}>
            {dnd.activeCard ? (
              <div className={styles['overlay']}>
                <IssueCardView issue={dnd.activeCard} />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <IssuePanel
        issueKey={issueKey}
        projectKey={key}
        onClose={() => patchParams({ issue: null })}
      />
    </div>
  );
}
